/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { get, map } from 'lodash';
import isEmail from 'validator/lib/isEmail';

import useFluxibleAction from 'js/lib/fluxibleHooks/useFluxibleAction';
import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';

import {
  ButtonGroup,
  Chip,
  ChipGroup,
  Dialog,
  FormValidationStatus,
  MultiSelect,
  TextField,
  Typography2,
} from '@coursera/cds-core';

import { compareByNaturalSortOrder } from 'bundles/author-common/utils/common';
import PartnerHelpLink from 'bundles/authoring/common/components/PartnerHelpLink';
import { useNotifications } from 'bundles/staff-management/src/common/notifications/Notifications';
import { StaffManagementStoreV2, type StaffMembershipType, actions } from 'bundles/staff-management/src/store';
import { getRoles } from 'bundles/staff-management/src/utils/roleUtils';

import _t from 'i18n!nls/staff-management';

const styles = {
  content: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-300);
  `,
  roles: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);

    .cds-selectController-outlined {
      max-width: unset;
    }
  `,
  emailErrors: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
  `,
};

export default function InviteStaffDialog() {
  const [emailInput, setEmailInput] = React.useState<string>('');
  const [emails, setEmails] = React.useState<string[]>([]);
  const [roles, setRoles] = React.useState<string[]>([]);
  const [emailErrors, setEmailErrors] = React.useState<{ [email: string]: string }>({});
  const resetState = React.useCallback<() => void>(() => {
    setEmailInput('');
    setEmails([]);
    setRoles([]);
    setEmailErrors({});
  }, []);
  const executeAction = useFluxibleAction();
  const { notify } = useNotifications();
  const { partnerId, type, open, onClose, onConfirm, roleOptions } = useFluxibleStore<
    typeof StaffManagementStoreV2,
    {
      open: boolean;
      partnerId: string | number | undefined;
      type: StaffMembershipType | undefined;
      roleOptions: { label: string; id: string }[];
      onClose: () => void;
      onConfirm: (input: { roles: string[]; emails: string[] }) => void;
    }
  >(StaffManagementStoreV2, (store) => {
    const templates = store.partnerRoleTemplates();
    return {
      partnerId: store.get().partnerId,
      type: store.get().type,
      open: store.get().ui?.addFlow?.show ?? false,

      roleOptions: map(get(templates, 'templates'), (t) => ({
        label: t.name,
        id: t.id,
      })).sort((a, b) => compareByNaturalSortOrder(b.label.toLowerCase(), a.label.toLowerCase())),
      onClose: () => {
        executeAction(actions.addFlow.prompt, { show: false });
        resetState();
      },
      onConfirm: (input: { roles: string[]; emails: string[] }) => {
        executeAction(actions.addFlow.confirm, {
          fromId: store.get().fromId ?? '',
          type: store.get().type ?? 'partner',
          roles: input.roles,
          users: input.emails.map((email) => ({ email })),
          notify,
        });
        resetState();
      },
    };
  });
  React.useEffect(() => {
    if (partnerId && type) {
      executeAction(actions.rolesOptions.load, { partnerId, type });
    }
  }, [executeAction, partnerId, type]);
  const roleTemplates = getRoles();

  return (
    <Dialog open={open} onClose={onClose}>
      <Dialog.HeadingGroup supportText={_t('Type or paste in emails below, separated by commas')}>
        {_t('Invite Staff')}
      </Dialog.HeadingGroup>
      <Dialog.Content css={styles.content}>
        <TextField
          className="pii-hide"
          label={_t('Email addresses')}
          placeholder={_t('Add emails')}
          fullWidth
          value={emailInput}
          onChange={(event) => setEmailInput(event.target.value)}
          onBlur={() => {
            const emailsToAdd = emailInput
              .split(',')
              .map((email) => email.trim())
              .filter(
                (email, index, allNewEmails) =>
                  email && !emails.includes(email) && allNewEmails.indexOf(email) === index
              );
            setEmails((_) => [..._, ...emailsToAdd]);
            setEmailInput('');
            setEmailErrors((currentErrors) => ({
              ...currentErrors,
              ...emailsToAdd
                .filter((email) => !isEmail(email))
                .reduce((acc, email) => ({ ...acc, [email]: _t('#{email} is not a valid email', { email }) }), {}),
            }));
          }}
        />
        {emails.length > 0 && (
          <ChipGroup aria-label={_t('Emails to invite')} variant="delete">
            {emails.map((email) => (
              <Chip
                key={email}
                onClick={() => {
                  setEmails(emails.filter((e) => e !== email));
                  const newErrors = { ...emailErrors };
                  delete newErrors[email];
                  setEmailErrors(newErrors);
                }}
                className="pii-hide"
              >
                {email}
              </Chip>
            ))}
          </ChipGroup>
        )}
        {Object.keys(emailErrors).length > 0 && (
          <div css={styles.emailErrors}>
            {Object.values(emailErrors).map((error) => (
              <FormValidationStatus key={error} validationStatus="error" label={error} />
            ))}
          </div>
        )}
        <div css={styles.roles}>
          <Typography2 variant="subtitleMedium" component="label" htmlFor="role-select">
            {_t('Role(s)')}
          </Typography2>
          <MultiSelect
            placeholder={_t('Role(s)')}
            id="role-select"
            value={roles}
            onChange={(value) => setRoles(Array.from(value) as string[])}
          >
            {roleOptions.map(({ id, label }) => (
              <MultiSelect.Option key={id} supportText={roleTemplates[id]?.description}>
                {label}
              </MultiSelect.Option>
            ))}
          </MultiSelect>
          <PartnerHelpLink linkText={_t('Learn more about staff roles')} articleId="360044740752" />
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <ButtonGroup
          primaryButtonProps={{
            children: _t('Invite'),
            onClick: () => onConfirm({ roles, emails }),
            disabled: emails.length === 0 || roles.length === 0 || Object.keys(emailErrors).length > 0,
          }}
          alternateButtonProps={{
            variant: 'secondary',
            children: _t('Cancel'),
            onClick: onClose,
          }}
        />
      </Dialog.Actions>
    </Dialog>
  );
}
